import React from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  useMetricsUi,
  headCellSx,
  numCellSx,
  textCellSx,
  SectionHead,
} from "./metricsUi";
import { formatMetricCell } from "./metricsFormat";

/**
 * Fiscal-year rollups from seeded period history (partial years labeled).
 * Click a year to drill into its periods.
 */
const FiscalYearHistoryTable = ({
  yearHistory = [],
  selectedYearId,
  onSelectYear,
  title = "Fiscal year performance",
  subtitle = "Aggregated from this client's available fiscal years. Partial / in-progress years are labeled — totals are not extrapolated to a full year. Select a year to open its periods.",
  yearColumnLabel = "Fiscal Year",
}) => {
  const ui = useMetricsUi();

  if (!yearHistory.length) return null;

  const rows = [...yearHistory].reverse();

  return (
    <Box>
      <SectionHead title={title} subtitle={subtitle} />

      <TableContainer sx={{ ...ui.card, overflow: "hidden" }}>
        <Table size="small" sx={{ minWidth: 720 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx(ui)}>{yearColumnLabel}</TableCell>
              <TableCell sx={headCellSx(ui)}>Coverage</TableCell>
              <TableCell sx={headCellSx(ui, { textAlign: "right" })}>
                Gross Sales
              </TableCell>
              <TableCell sx={headCellSx(ui, { textAlign: "right" })}>
                Orders
              </TableCell>
              <TableCell sx={headCellSx(ui, { textAlign: "right" })}>
                Units Sold
              </TableCell>
              <TableCell sx={headCellSx(ui, { textAlign: "right" })}>
                UPT
              </TableCell>
              <TableCell sx={headCellSx(ui, { textAlign: "right" })}>
                Gross AOV
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const selected = row.year_id === selectedYearId;
              return (
                <TableRow
                  key={row.year_id}
                  hover
                  onClick={() => onSelectYear?.(row)}
                  sx={{
                    cursor: onSelectYear ? "pointer" : "default",
                    backgroundColor: selected ? ui.accentSoft : "transparent",
                    "&:hover td": { backgroundColor: ui.surfaceAlt },
                    "&:last-child td": { borderBottom: "none" },
                  }}
                >
                  <TableCell sx={textCellSx(ui)}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                      {row.label}
                    </Typography>
                    <Typography
                      sx={{ fontSize: 11.5, color: ui.colors.textTertiary }}
                    >
                      {row.start_date} – {row.end_date}
                    </Typography>
                  </TableCell>
                  <TableCell sx={textCellSx(ui)}>
                    {row.is_partial ? (
                      <Box
                        component="span"
                        sx={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: ui.warning,
                          backgroundColor: ui.warningBg,
                          borderRadius: "10px",
                          px: 1,
                          py: 0.25,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.coverage_note || "Partial year"}
                      </Box>
                    ) : (
                      <Typography
                        sx={{ fontSize: 12, color: ui.colors.textSecondary }}
                      >
                        Full year
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={numCellSx(ui, { fontWeight: 600, fontSize: 14 })}>
                    {formatMetricCell(row.gross_sales, "currency")}
                  </TableCell>
                  <TableCell sx={numCellSx(ui)}>
                    {formatMetricCell(row.orders, "count")}
                  </TableCell>
                  <TableCell sx={numCellSx(ui)}>
                    {formatMetricCell(row.units_sold, "count")}
                  </TableCell>
                  <TableCell sx={numCellSx(ui)}>
                    {formatMetricCell(row.upt, "ratio")}
                  </TableCell>
                  <TableCell sx={numCellSx(ui)}>
                    {formatMetricCell(row.gross_aov, "currency2")}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default FiscalYearHistoryTable;
